import {EApiKeyType, ECallbackType, EIntegrationSettingsTab} from './definitions';

export default {
  tabs: {
    [EIntegrationSettingsTab.MASTER]: 'Main Profile',
    [EIntegrationSettingsTab.API_KEY]: 'API key',
    [EIntegrationSettingsTab.CALLBACK]: 'Webhook',
  },
  masterSettings: {
    callbackSettings: 'Webhook URL setup',
    addCallback: 'Add a webhook URL',
    changeCallback: 'Change URL',
    modalNote: 'URL verification is required for the settings to work correctly',
    modalText: 'Webhook URL — HTTPS only, port 443',
    errorMessage: 'The requested resource does not exist',
    jivoConnectedMessage:
      'You cannot change the webhook URL, since you have an available Jivo module connected.',
    checkMessage:
      'To check availability, we will send a HEAD request and expect a response with status code 200. Otherwise, we will not be able to register your webhook URL.',
    callbackUrlType: {
      [ECallbackType.IN_MESSAGE]: {
        title: 'Webhook URL for incoming messages',
        description: 'At this URL, you can receive incoming messages from clients.',
        add: {
          title: 'Add a webhook URL for incoming messages',
        },
        change: {
          title: 'Change the webhook URL for incoming messages?',
        },
        delete: {
          title: 'Delete the webhook URL for incoming messages?',
          notification: 'The webhook URL for incoming messages has been deleted successfully',
        },
      },
      [ECallbackType.STATUS]: {
        title: 'Webhook URL for statuses',
        description: 'At this URL, you can receive statuses of sent broadcasts.',
        add: {
          title: 'Add a webhook URL for broadcast statuses',
        },
        change: {
          title: 'Change the webhook URL for broadcast statuses?',
        },
        delete: {
          title: 'Delete the webhook URL for statuses?',
          notification: 'The webhook URL for statuses has been deleted successfully',
        },
      },
      [ECallbackType.MESSAGE_MATCHER]: {
        title: 'Webhook URL for template registration status updates',
        description:
          'At this URL, you can receive information about status updates for templates submitted for registration.',
        add: {
          title: 'Add a webhook URL for template registration status updates',
        },
        change: {
          title: 'Change the webhook URL for template registration status updates?',
        },
        delete: {
          title: 'Delete the webhook URL for template registration status updates?',
          notification:
            'The webhook URL for template registration status updates has been deleted successfully',
        },
      },
      [ECallbackType.MESSAGE_MATCHER_CATEGORY_CHANGED]: {
        title: 'Webhook URL for WhatsApp template category changes',
        description:
          'At this URL, you can receive information about WhatsApp template category updates from Meta.',
        add: {
          title: 'Add a webhook URL for WhatsApp template category changes',
        },
        change: {
          title: 'Change the webhook URL for WhatsApp template category changes?',
        },
        delete: {
          title: 'Delete the webhook URL for WhatsApp template category changes?',
          notification:
            'The webhook URL for WhatsApp template category changes has been deleted successfully',
        },
      },
      [ECallbackType.SHORT_LINK_VISITED]: {
        title: 'Webhook URL for tracking clicks on the WhatsApp link button',
        description:
          'At this URL, you can receive information about recipient clicks on link buttons in WhatsApp channels if you use templates with click tracking.',
        add: {
          title: 'Add a webhook URL for tracking clicks on the WhatsApp link button',
        },
        change: {
          title: 'Change the webhook URL for tracking clicks on the WhatsApp link button?',
        },
        delete: {
          title: 'Delete the webhook URL for tracking clicks on the WhatsApp link button?',
          notification:
            'The webhook URL for tracking clicks on the WhatsApp link button has been deleted successfully',
        },
      },
      [ECallbackType.SENDER_STATUS]: {
        title: 'Webhook URL for sender status changes',
        description:
          'You can receive information about sender status changes (created/locked/unlocked) at the specified URL.',
        add: {
          title: 'Add a webhook URL for sender status changes',
        },
        change: {
          title: 'Change the webhook URL for sender status changes?',
        },
        delete: {
          title: 'Delete the webhook URL for sender status changes?',
          notification: 'The webhook URL for sender status changes has been deleted successfully',
        },
      },
    },
    yourAPIKey: 'Your API key',
    APIKeyDescription:
      "You can integrate edna Pulse with any app by accessing broadcasts via APIs. For more information about API methods,<br/> refer to <0>the developer's manual</0>.",
    generateAPIKey: 'Generate an API key',
    generateNewAPIKey: 'New API key',
    callbackAuth: 'Webhook request authentication',
    callbackAuthHint:
      'You can enable the authentication of webhook requests sent from edna Pulse to your server. Select the checkbox to generate an API key. edna Pulse will use this key when sending a webhook to your API endpoint.',
    callbackAuthSwitch: 'Authenticate webhooks from edna Pulse',
    generateNewCallbackKey: 'New webhook key',
    authErrorText: "Couldn't complete the request. Try again later.",
    connected: 'Connected',
  },
  changeApiKeyModal: {
    title: 'Change the API key?',
    message:
      'Once you change the API key, the traffic you receive through it will stop. Copy the new key and paste it into your system for it to work correctly.',
  },
  authCallbackKeyModal: {
    title: 'Copy your API key',
    warning:
      'Copy your API key now and share it with a developer or store it safely. You can see and copy the whole key only once. To secure your data, the key will be hidden after that.',
  },
  apiKeySettings: {
    title: 'Additional API Keys',
    generate: 'Generate',
    keyName: 'API Key Name',
    keyValue: 'Key',
    validTo: 'Valid until',
    status: 'State',
    permissions: 'Allowed Methods',
    subjects: 'Connected Senders',
    subjectStatus: 'Sender Status',
  },
  apiKeyType: {
    [EApiKeyType.MESSAGE_HISTORY]: 'Message History',
    [EApiKeyType.CHANNEL_PROFILE]: 'Channels',
    [EApiKeyType.CASCADE]: 'Cascades',
    [EApiKeyType.CALLBACK]: 'Webhooks',
    [EApiKeyType.OUT_MESSAGES]: 'Messaging',
    [EApiKeyType.MESSAGE_MATCHERS]: 'Templates',
    [EApiKeyType.IN_MESSAGE]: 'Read Receipts',
    [EApiKeyType.CHATBOTS]: 'Scenarios',
  },
  apiKeyForm: {
    keyData: 'API Key',
    keyName: 'API Key Name',
    keyNameHint: 'Internal name, helps identify additional API keys in the list',
    params: 'Key parameters',
    validTo: 'API key expiration',
    unlimitedValidity: 'Unlimited',
    limitedValidity: 'Valid until',
    subjects: 'Senders',
    select: 'Select',
    permissions: 'Access to API methods',
    selectPermissions: 'Select the required methods',
  },
  apiKeyDeleteModal: {
    title: 'Delete API key?',
    description: 'This will delete the unique identifier used to access edna Pulse',
  },
  callbackSettings: {
    title: 'Additional webhooks',
    addCallback: 'Add webhook',
    name: 'Webhook name',
    url: 'URL',
    status: 'State',
    hasAuthCallback: 'edna request authentication is enabled',
    subjects: 'Connected Senders',
    subjectStatus: 'Sender Status',
    types: 'Connected webhooks',
  },
  callbackType: {
    [ECallbackType.STATUS]: 'Webhook for statuses',
    [ECallbackType.IN_MESSAGE]: 'Webhook for incoming messages',
    [ECallbackType.MESSAGE_MATCHER]: 'Webhook for template registration status updates',
    [ECallbackType.MESSAGE_MATCHER_CATEGORY_CHANGED]:
      'Webhook for WhatsApp template category changes',
    [ECallbackType.SHORT_LINK_VISITED]: 'Webhook for tracking clicks on the WhatsApp link button',
    [ECallbackType.SENDER_STATUS]: 'Webhook for sender status changes',
  },
  callbackForm: {
    data: 'Webhook data',
    dataEdit: 'Edit webhook data',
    name: 'Webhook name',
    nameHint: 'Internal name, helps identify additional webhooks in the list',
    url: 'URL address',
    urlHint: 'Your server URL that will receive webhook event notifications',
    additionalUrl: 'Additional URL (optional)',
    authCallbackEdit: 'Edit request authentication',
    authCallback: 'Request authentication',
    regenerateKey: 'Regenerate key',
    authCallbackHint:
      'Request authentication is required to verify that data comes from a trusted source\n' +
      'Enable the checkbox to generate an API key. edna Pulse will use it when sending webhook requests to your endpoint',
    authCallbackToggle: 'edna Pulse request authentication',
    authCallbackActive:
      'API key connected. edna Pulse will use it when sending webhook requests to your endpoint',
    generateAuthCallbackKey: 'New webhook request key',
    params: 'Webhook parameters',
    paramsEdit: 'Edit webhook parameters',
    subjects: 'Senders',
    types: 'Select webhook types',
    selectTypes: 'Select the required methods and enter the URL',
    successNotification: 'Webhook updated successfully',
  },
  callbackDeleteModal: {
    title: 'Delete webhook?',
    description: 'This webhook will be permanently deleted',
  },
  subjectLockedStatus: {
    blocked: 'Blocked',
    active: 'Active',
  },
};
